"use client";

import { useState, useRef, useEffect } from "react";
import { ChevronLeft, ChevronRight, Calendar } from "lucide-react";

interface RentalDatePickerProps {
  value: string;
  onChange: (date: string) => void;
  min?: string;
  disableWeekends?: boolean;
  closedDays?: number[]; // 0=Sun, 1=Mon, ..., 6=Sat
  placeholder?: string;
  className?: string;
  triggerClassName?: string;
  dropdownAlign?: "left" | "right";
}

function formatDateFr(dateStr: string): string {
  if (!dateStr) return "";
  const d = new Date(`${dateStr}T00:00:00`);
  return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" });
}

function toDateStr(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function RentalDatePicker({
  value,
  onChange,
  min,
  disableWeekends = false,
  closedDays,
  placeholder = "Sélectionner une date",
  className = "",
  triggerClassName = "",
  dropdownAlign = "left",
}: RentalDatePickerProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Current displayed month
  const initial = value ? new Date(`${value}T00:00:00`) : new Date();
  const [viewYear, setViewYear] = useState(initial.getFullYear());
  const [viewMonth, setViewMonth] = useState(initial.getMonth());

  // Close on click outside
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const prevMonth = () => {
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear(viewYear - 1);
    } else {
      setViewMonth(viewMonth - 1);
    }
  };

  const nextMonth = () => {
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear(viewYear + 1);
    } else {
      setViewMonth(viewMonth + 1);
    }
  };

  // Build calendar grid
  const firstDay = new Date(viewYear, viewMonth, 1);
  const startWeekday = (firstDay.getDay() + 6) % 7; // Mon=0
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();

  const minDate = min || toDateStr(new Date());

  const cells: { day: number; dateStr: string; disabled: boolean; isWeekend: boolean; isToday: boolean; isSelected: boolean }[] = [];

  for (let i = 0; i < startWeekday; i++) {
    cells.push({ day: 0, dateStr: "", disabled: true, isWeekend: false, isToday: false, isSelected: false });
  }

  const todayStr = toDateStr(new Date());

  for (let d = 1; d <= daysInMonth; d++) {
    const date = new Date(viewYear, viewMonth, d);
    const dateStr = toDateStr(date);
    const dayOfWeek = date.getDay(); // 0=Sun, 6=Sat
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
    const isPast = dateStr < minDate;
    const isClosed = closedDays ? closedDays.includes(dayOfWeek) : (disableWeekends && isWeekend);
    const disabled = isPast || isClosed;

    cells.push({
      day: d,
      dateStr,
      disabled,
      isWeekend,
      isToday: dateStr === todayStr,
      isSelected: dateStr === value,
    });
  }

  const monthNames = [
    "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
    "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre",
  ];

  return (
    <div className={`relative ${className}`} ref={ref}>
      {/* Trigger */}
      <button
        type="button"
        onClick={() => {
          setOpen(!open);
          if (!open && value) {
            const d = new Date(`${value}T00:00:00`);
            setViewYear(d.getFullYear());
            setViewMonth(d.getMonth());
          }
        }}
        className={`w-full flex items-center gap-2 h-10 rounded-md border border-input bg-background px-3 text-sm ${
          value ? "text-foreground" : "text-gray-400"
        } ${triggerClassName}`}
      >
        <Calendar className="h-4 w-4 text-gray-400 flex-shrink-0" />
        <span className="flex-1 text-left">{value ? formatDateFr(value) : placeholder}</span>
      </button>

      {/* Dropdown calendar */}
      {open && (
        <div className={`absolute top-full mt-1 bg-white rounded-xl shadow-xl border z-50 p-3 w-[280px] ${dropdownAlign === "right" ? "right-0" : "left-0"}`}>
          {/* Header */}
          <div className="flex items-center justify-between mb-2">
            <button type="button" onClick={prevMonth} className="p-1 hover:bg-gray-100 rounded">
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="text-sm font-semibold">
              {monthNames[viewMonth]} {viewYear}
            </span>
            <button type="button" onClick={nextMonth} className="p-1 hover:bg-gray-100 rounded">
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>

          {/* Day names */}
          <div className="grid grid-cols-7 gap-0 mb-1">
            {["Lu", "Ma", "Me", "Je", "Ve", "Sa", "Di"].map((d) => (
              <div key={d} className="text-center text-xs font-medium text-gray-400 py-1">
                {d}
              </div>
            ))}
          </div>

          {/* Days grid */}
          <div className="grid grid-cols-7 gap-0">
            {cells.map((cell, i) => {
              if (cell.day === 0) {
                return <div key={`empty-${i}`} />;
              }
              return (
                <button
                  key={cell.dateStr}
                  type="button"
                  disabled={cell.disabled}
                  onClick={() => {
                    onChange(cell.dateStr);
                    setOpen(false);
                  }}
                  className={`h-8 w-full text-sm rounded-md transition-colors ${
                    cell.isSelected
                      ? "bg-secondary text-white font-semibold"
                      : cell.disabled
                        ? cell.isWeekend && disableWeekends
                          ? "text-gray-300 bg-gray-50 line-through cursor-not-allowed"
                          : "text-gray-300 cursor-not-allowed"
                        : cell.isToday
                          ? "bg-secondary/10 text-secondary font-semibold hover:bg-secondary/20"
                          : cell.isWeekend
                            ? "text-gray-500 hover:bg-gray-100"
                            : "text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  {cell.day}
                </button>
              );
            })}
          </div>

          {/* Legend */}
          {disableWeekends && (
            <p className="text-[10px] text-gray-400 mt-2 text-center">
              Les week-ends ne sont pas facturés pour ce vendeur
            </p>
          )}
        </div>
      )}
    </div>
  );
}
